import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articleMap, articles } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? articleMap[id] : undefined;

  if (!article) {
    return (
      <>
        <SEOHead title="文章未找到" description="您访问的文章不存在或已被移除。" />
        <ScrollToTop />
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 font-medium text-sm rounded-full whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categoryMap[article.categorySlug];
  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.summary}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://zylogin.github.io/article/${article.id}`}
        ogType="article"
      />
      <ScrollToTop />
      <Navbar />
      <main>
        <div className="pt-20 pb-4 bg-gradient-to-b from-background-100 to-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: category?.name || '分类', path: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full py-8 md:py-12 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <header className="mb-10">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="text-xs px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 font-medium whitespace-nowrap"
                >
                  {category?.name}
                </Link>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.readCount.toLocaleString()} 阅读
                </span>
              </div>
            </header>

            <div
              className="prose prose-sm md:prose-base max-w-none mb-14
                prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-semibold
                prose-p:text-foreground-700 prose-p:leading-relaxed
                prose-h2:text-xl prose-h2:mt-10 prose-h2:mb-4
                prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-3
                prose-strong:text-foreground-900
                prose-a:text-primary-600 prose-a:no-underline hover:prose-a:text-primary-700"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {relatedArticles.length > 0 && (
              <div className="border-t border-background-200 pt-10">
                <h2 className="font-heading font-semibold text-foreground-900 mb-6 text-lg">
                  相关文章推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group p-5 rounded-xl border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                    >
                      <h3 className="font-medium text-foreground-900 text-sm mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2 mb-3">{ra.summary}</p>
                      <div className="flex items-center gap-2 text-xs text-foreground-400">
                        <span>{ra.publishDate}</span>
                        <span>·</span>
                        <span>{ra.readCount.toLocaleString()} 阅读</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.summary,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            datePublished: article.publishDate,
            url: `https://zylogin.github.io/article/${article.id}`,
            publisher: {
              '@type': 'Organization',
              name: '众赢zygjb',
              url: 'https://zylogin.github.io',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://zylogin.github.io' },
              {
                '@type': 'ListItem',
                position: 2,
                name: category?.name || '分类',
                item: `https://zylogin.github.io/category/${article.categorySlug}`,
              },
              { '@type': 'ListItem', position: 3, name: article.title },
            ],
          }),
        }}
      />
    </>
  );
}